import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { TopBarProps } from "../types";
import { EXTERNAL_LINKS } from "../services/api";
import { hasRole } from "../types/tenant";
import "../styles/TopBar.css";
import flagLogo from "../assets/flag_logo.png";

const TopBar: React.FC<TopBarProps> = ({ showLogo = true }) => {
  const { t } = useTranslation('common');
  const { t: tTenant } = useTranslation('tenant');
  const [showDropdown, setShowDropdown] = useState(false);
  const { user, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use help_manual_url from user profile (set by admin in system settings)
  // Falls back to hardcoded URL if the setting hasn't been configured
  const helpManualUrl = user?.help_manual_url !== undefined
    ? user.help_manual_url
    : EXTERNAL_LINKS.HELP_MANUAL;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
  };

  return (
    <div className="topbar">
      <div className="topbar-left">
        {showLogo && (
          <div className="logo">
            <img src={flagLogo} alt={t('topBar.logoAlt')} className="logo-image" />
          </div>
        )}
      </div>

      <div className="topbar-right">
        {helpManualUrl && (
        <a
          href={helpManualUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="help-btn"
          title={t('topBar.help', { defaultValue: 'Help' })}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </a>
        )}
        <div className="user-menu" ref={dropdownRef}>
          <div
            className="user-avatar"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            {user?.username?.charAt(0).toUpperCase() || "U"}
          </div>

          {showDropdown && (
            <div className="dropdown-menu">
              <div className="dropdown-header">
                <div className="user-avatar-dropdown">
                  {user?.username?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.full_name || t('topBar.userFallback')}</div>
                  <div className="user-email">
                    {user?.email || t('topBar.emailFallback')}
                  </div>
                </div>
              </div>

              {(user?.usage || user?.quotas) && (
                <div className="dropdown-usage">
                  <div className="usage-title">{t('topBar.usage')}</div>

                  {/* Pages Usage (Lifetime). When the allowance breakdown is
                      present it is authoritative: it already accounts for
                      pages reserved for other members, which the raw limit
                      minus usage would overstate. */}
                  <div className="usage-item">
                    <div className="usage-label">{t('topBar.pages', { defaultValue: 'Pages' })}</div>
                    <div className="usage-value">
                      {user.quota?.used ?? user.usage?.total_pages_processed ?? 0}
                      {(user.quota?.pending ?? user.usage?.total_pages_pending) ? ` (+${user.quota?.pending ?? user.usage?.total_pages_pending} ${t('topBar.pending', { defaultValue: 'pending' })})` : ''}
                      / {(user.quota ? user.quota.limit : user.total_page_limit) ?? t('topBar.unlimited')}
                    </div>
                  </div>
                  {(user.quota ? user.quota.limit != null : user.total_page_limit != null) && (
                    <div className="usage-remaining">
                      {t('topBar.remainingPages', { defaultValue: '残りページ数' })}:&nbsp;
                      {user.quota
                        ? user.quota.available ?? 0
                        : Math.max(0, (user.total_page_limit as number) - (user.usage?.total_pages_processed || 0) - (user.usage?.total_pages_pending || 0))}
                    </div>
                  )}


                  {/* Account Expiry */}
                  <div className="usage-item">
                    <div className="usage-label">{t('topBar.expiry')}</div>
                    <div className="usage-value">
                      {user.account_end_date ? new Date(user.account_end_date).toLocaleDateString() : '-'}
                    </div>
                  </div>
                </div>
              )}

              <div className="dropdown-divider"></div>

              <button
                className="dropdown-item"
                onClick={() => {
                  window.location.href = "/account/audit-logs";
                }}
              >
                {t('topBar.auditLogs', { defaultValue: 'ログイン履歴' })}
              </button>

              {hasRole(user?.tenant_role ?? undefined, "admin") && (
                <>
                  <div className="dropdown-divider"></div>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      window.location.href = "/team";
                    }}
                  >
                    {tTenant('nav.team')}
                  </button>
                </>
              )}

              {hasRole(user?.tenant_role ?? undefined, "super_admin") && (
                <>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      window.location.href = "/team/groups";
                    }}
                  >
                    {tTenant('nav.groups')}
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      window.location.href = "/team/audit-logs";
                    }}
                  >
                    {tTenant('nav.auditLog')}
                  </button>
                </>
              )}

              {user?.is_superuser && (
                <div className="dropdown-divider"></div>
              )}

              {user?.is_superuser && (
                <button
                  className="dropdown-item admin-btn"
                  onClick={() => {
                    // Navigate to admin (we will implement this route)
                    window.location.href = "/admin/users";
                  }}
                >
                  {t('topBar.adminPanel')}
                </button>
              )}

              <div className="dropdown-divider"></div>

              <button
                className="dropdown-item logout-btn"
                onClick={handleLogout}
              >
                {t('topBar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;